import type { ConfirmSpec } from '../../SelectionActionBar';
import type { Patient } from '../../../store/slices/patientsSlice';
import type { PatientExternalEmrImportStartResult } from '../../../services/externalEmrPatients';
import { getPatientExternalEmrImportStatus } from '../../../hooks/usePatientExternalEmrImport';
import {
  canStartFromStatus,
  externalEmrCardStatus,
  isMovingStatus,
  type ExternalEmrCardStatus,
  type ExternalEmrPatientState,
} from '../../../services/externalEmrPatients';
import {
  BULK_PRIORITY_LINE,
  CONFIRM_START_ACCEPT,
  bulkConfirmTitle,
  bulkCutoverLine,
  bulkMidEpisodeLine,
} from './externalEmrPatientCopy';

export type ExternalPatient = Patient & {
  external_emr: ExternalEmrPatientState;
};

export function isExternalPatient(
  patient: Patient
): patient is ExternalPatient {
  return !!patient.external_emr;
}

/** What the card is showing now: the poll's word over the list's. */
export function liveCardStatus(
  patient: ExternalPatient
): ExternalEmrCardStatus {
  return externalEmrCardStatus(
    patient.external_emr,
    // The card's poll when it has one, else what the list carried.
    getPatientExternalEmrImportStatus(patient.id) ?? patient.external_emr.import
  );
}

export const startable = (items: ExternalPatient[]) =>
  items.filter(p => canStartFromStatus(liveCardStatus(p)));
// A queued or running refresh of an imported chart cancels the same way.
export const cancellable = (items: ExternalPatient[]) =>
  items.filter(p => isMovingStatus(liveCardStatus(p)));

/** The confirm for a multi-select start: count, bulk priority, mid-episode. */
export function bulkStartConfirm(
  items: ExternalPatient[],
  takeOverV2: boolean
): ConfirmSpec {
  const toStart = startable(items);
  const midEpisode = toStart.filter(p => !p.external_emr.ready).length;
  const cutover = toStart.filter(
    p => p.external_emr.census_status === 'recertified_in_external_emr'
  ).length;
  return {
    title: bulkConfirmTitle(toStart.length),
    body: (
      <div className='space-y-2'>
        {/* One id runs at single priority on the server. */}
        {toStart.length > 1 && <p>{BULK_PRIORITY_LINE}</p>}
        {midEpisode > 0 && <p>{bulkMidEpisodeLine(midEpisode, takeOverV2)}</p>}
        {cutover > 0 && <p>{bulkCutoverLine(cutover)}</p>}
      </div>
    ),
    choices: [
      { key: 'cancel', label: 'Cancel' },
      { key: 'start', label: CONFIRM_START_ACCEPT },
    ],
  };
}

/**
 * Why charts could not start, for the toast's second line. The backend
 * writes each reason for users; the same reason is said once, and at most
 * three are shown.
 */
export function refusalReasons(
  rows: PatientExternalEmrImportStartResult[]
): string | undefined {
  const reasons = [
    ...new Set(
      rows
        .map(row => row.refused_reason?.trim())
        .filter((reason): reason is string => !!reason)
    ),
  ];
  if (reasons.length === 0) return undefined;
  return reasons.slice(0, 3).join(' ');
}
